import { TabsList, TabsTrigger } from '@ifixit/ui/components/tabs';
import { localizeAndFormat } from 'Shared/utils/numberFormat';
import { useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/ui/components/icon';

// Type definition for view tabs with extended properties
interface ViewTab {
   name: string;
   url: string;
   data?: any;
}

interface UserTabsProps {
   viewTabs: ViewTab[];
   notifBadge: number;
   setCurrentTab: (name: string) => void;
   tabIcons?: Record<string, string>;
}

// Map tab names from backend (PascalCase) to translation keys (camelCase)
const tabTranslationKeys = {
   'About': 'about',
   'Devices': 'devices',
   'Contributions': 'contributions',
   'Activity': 'activity',
   'Reputation': 'reputation',
   'Notifications': 'notifications',
   'Orders': 'orders',
   'Subscriptions': 'subscriptions',
} as const;

type TabTranslationKey = (typeof tabTranslationKeys)[keyof typeof tabTranslationKeys];

// Overrides the id Radix derives from baseId, so the panel has to name itself
// with this instead of Radix's aria-labelledby. Selenium selects on it too
// (Tests/Objects/UIExtensions/ProfileUIExtension.php).
export const userTabTriggerId = (tabName: string) => `${tabName.toLowerCase()}Tab`;

const getTabTranslationKey = (tabName: string): TabTranslationKey => {
   const key = tabTranslationKeys[tabName as keyof typeof tabTranslationKeys];
   return key ?? (tabName.toLowerCase() as TabTranslationKey);
};

export function UserTabs({ viewTabs, notifBadge, setCurrentTab, tabIcons }: UserTabsProps) {
   const t = useTranslations('UserPage');

   return (
      <TabsList className="gap-2.5 py-2 md:flex-col md:items-stretch">
         {viewTabs.map(tab => {
            const icon = tabIcons?.[tab.name];
            const badge =
               tab.name === 'Notifications' && notifBadge > 0
                  ? localizeAndFormat(notifBadge, { notation: 'compact' })
                  : null;

            return (
               <TabsTrigger
                  key={tab.name}
                  value={tab.name}
                  id={userTabTriggerId(tab.name)}
                  onClick={() => {
                     if (tab.name === 'Subscriptions') {
                        window.location.assign(tab.url);
                        return;
                     }
                     setCurrentTab(tab.name);
                     window.history.replaceState(null, '', tab.url + window.location.search);
                  }}
                  className="justify-start rounded-2xl border border-gray-300 px-3 md:py-3 hover:border-gray-400 data-[state=active]:cursor-default data-[state=active]:border-gray-100 data-[state=active]:bg-gray-100"
               >
                  {icon && <FaIcon name={icon} />}
                  <span>{t(getTabTranslationKey(tab.name))}</span>
                  {badge && (
                     <span className="rounded-full bg-brand-500 px-1.5 text-xs font-semibold text-white tabular-nums">
                        {badge}
                     </span>
                  )}
               </TabsTrigger>
            );
         })}
      </TabsList>
   );
}
